import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { buildProductUrl } from "../../utils/navigation";
import {
  Search,
  Heart,
  ShoppingCart,
  User,
  Phone,
  Mail,
  Menu,
  Loader2,
  LogOut,
  Truck,
} from "lucide-react";
import { useCategoriesWithProducts } from "../../hooks/useSupabase";
import { useCartStore } from "../../store/cartStore";
import { getProducts } from "../../services/products.service";
import { useAuth } from "../../hooks/useAuth";
import { useWishlistContext } from "../../hooks/useWishlistContext";
import MobileHeader from "./MobileHeader";
import { getPrimaryProductImageUrl } from "../../utils";
import Categories from "./Categories";
import headerConfig from "./headerConfig";
import { logSearchActivity } from "../../services/analytics.service";

const Header = ({
  isCategoriesVisible = headerConfig.showCategories,
}: {
  isCategoriesVisible?: boolean;
}) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [isMobile, setIsMobile] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotalCount, setSearchTotalCount] = useState(0);

  const { wishlistedIds } = useWishlistContext();
  const wishlistCount = wishlistedIds.length;

  const cartCount = useCartStore((s) =>
    s.items.reduce((sum, item) => sum + item.quantity, 0)
  );

  const {
    categoriesWithSubs,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCategoriesWithProducts();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".user-menu-container")) setIsUserMenuOpen(false);
    };
    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  useEffect(() => {
    setSearchLoading(true);
    const delayedSearch = setTimeout(() => {
      if (searchQuery.length > 2 && searchQuery.trim()) {
        getProducts({ search: searchQuery.trim(), limit: 20, offset: 0 })
          .then(({ products, totalCount }) => {
            setSearchResults(products);
            setSearchTotalCount(totalCount);
            setShowSearchResults(true);
            setSearchLoading(false);
          })
          .catch(() => {
            setSearchResults([]);
            setSearchTotalCount(0);
            setShowSearchResults(false);
            setSearchLoading(false);
          });
      } else {
        setShowSearchResults(false);
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchLoading(false);
      }
    }, 300);
    return () => { clearTimeout(delayedSearch); setSearchLoading(false); };
  }, [searchQuery]);

  const handleWishlistClick = () => {
    if (!user) { navigate("/registration"); return; }
    navigate("/wishlist");
  };
  const handleCartClick = () => {
    if (!user) { navigate("/registration"); return; }
    navigate("/cart");
  };
  const handleTrackingClick = () => {
    if (!user) { navigate("/registration"); return; }
    navigate("/tracking");
  };
  const handleSearchEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.length > 2) {
      setShowSearchResults(false);
      logSearchActivity({ query: searchQuery, eventType: "search", resultCount: searchTotalCount, userId: user?.id });
      navigate(`/products?search=${encodeURIComponent(searchQuery)}&sort=popular`, {
        state: { searchQuery, sort: "popular" },
      });
      setSearchQuery("");
    }
  };

  const handleSearchButtonClick = () => {
    if (searchQuery.length > 2) {
      setShowSearchResults(false);
      logSearchActivity({ query: searchQuery, eventType: "search", resultCount: searchTotalCount, userId: user?.id });
      navigate(`/products?search=${encodeURIComponent(searchQuery)}&sort=popular`, {
        state: { searchQuery, sort: "popular" },
      });
      setSearchQuery("");
    }
  };

  void isMobileMenuOpen;
  void isMobileSearchOpen;

  if (isMobile) return <MobileHeader />;

  return (
    <div className="w-full bg-white">

      {/* ── Row 1: utility bar ── */}
      <div className="border-b border-header-border">
        <div className="py-1.75 px-10 hidden md:flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-6 ">
            <a
              href="tel:+96181389098"
              className="flex items-center gap-1.5 text-[11px] text-header-muted hover:text-primary transition-colors"
            >
              <Phone className="w-2.75 h-2.75" />
              +961 81 389 098
            </a>
            <span className="w-px h-2.5 bg-header-border" />
            <a
              href="mailto:sales@myperfume.group"
              className="flex items-center gap-1.5 text-[11px] text-header-muted hover:text-primary transition-colors"
            >
              <Mail className="w-2.75 h-2.75" />
              sales@myperfume.group
            </a>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="text-[11px] text-header-muted">Follow us</span>
            <a
              href="https://www.facebook.com/myperfume.group"
              target="_blank"
              rel="noopener noreferrer"
              className="w-5.5 h-5.5 rounded-full bg-[#1877f2] flex items-center justify-center hover:opacity-90 transition-opacity"
              aria-label="Facebook"
            >
              <span className="text-white text-[10px] font-bold leading-none">f</span>
            </a>
            <a
              href="https://www.instagram.com/myperfume.group"
              target="_blank"
              rel="noopener noreferrer"
              className="w-5.5 h-5.5 rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
              style={{ background: "linear-gradient(45deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)" }}
              aria-label="Instagram"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* ── Row 2: logo + search + icons ── */}
      <div className="border-b border-header-border">
        <div className="px-10 py-4 flex items-center gap-6 max-w-7xl mx-auto">

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            className="md:hidden flex items-center justify-center w-10 h-10"
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6 text-primary" />
          </button>

          {/* Logo */}
          <Link to="/" className="shrink-0">
            <div
              className="text-[26px] font-normal text-primary text-center leading-none tracking-[0.3px]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              My <span className="italic text-accent">Perfume</span>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <div className="h-px w-4.5 bg-accent" />
              <span className="text-[9px] text-header-muted tracking-[2px] uppercase">
                Signature Scent
              </span>
              <div className="h-px w-4.5 bg-accent" />
            </div>
          </Link>

          {/* Search */}
          <div className="flex-1 max-w-140 mx-auto relative hidden md:block">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for products, brands and more..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2.75 pr-12 border border-header-border bg-header-surface text-[13px] text-primary placeholder:text-header-muted focus:outline-none focus:border-primary transition-colors"
                style={{ borderRadius: 0 }}
                onFocus={() => searchQuery && setShowSearchResults(true)}
                onKeyDown={handleSearchEnter}
              />
              <button
                className="absolute right-0 top-0 bottom-0 w-11 bg-accent hover:bg-accent-dark flex items-center justify-center transition-colors"
                style={{ borderRadius: 0 }}
                onClick={handleSearchButtonClick}
              >
                {searchLoading
                  ? <Loader2 className="w-3.75 h-3.75 animate-spin text-primary" />
                  : <Search className="w-3.75 h-3.75 text-primary" />}
              </button>
            </div>

            {showSearchResults && (
              <div className="absolute top-full left-0 right-0 bg-white border border-header-border shadow-xl z-50 max-h-96 overflow-y-auto">
                <div className="p-2">
                  {searchResults.length > 0 ? (
                    <>
                      <div className="text-[11px] text-header-muted mb-2 px-2">
                        {searchResults.length} results found
                      </div>
                      {searchResults.slice(0, 16).map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center p-3 hover:bg-header-surface cursor-pointer transition-colors"
                          onClick={() => {
                            logSearchActivity({ query: searchQuery, eventType: "click", resultCount: searchTotalCount, clickedProductId: product.id, userId: user?.id });
                            setShowSearchResults(false);
                            setSearchQuery("");
                            navigate(buildProductUrl(product.id, product.name, product.brand_name));
                          }}
                        >
                          {product.image_paths?.length > 0 ? (
                            <img
                              src={getPrimaryProductImageUrl(product.image_paths) || ""}
                              alt={product.name}
                              className="w-12 h-12 object-cover mr-3 shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-header-surface mr-3 shrink-0 flex items-center justify-center">
                              <span className="text-header-muted text-xs">No Image</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-primary truncate">{product.name}</div>
                            <div className="text-[11px] text-header-muted truncate">{product.description}</div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="p-4 text-center text-header-muted">
                      <p className="text-[13px] font-medium">No products found</p>
                      <p className="text-[11px] mt-1">Try different keywords</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile search toggle */}
          <button
            onClick={() => setIsMobileSearchOpen((v) => !v)}
            className="md:hidden flex items-center justify-center w-10 h-10"
            aria-label="Search"
          >
            <Search className="w-5 h-5 text-primary" />
          </button>

          {/* Action icons */}
          <div className="flex items-center gap-5.5">

            <button
              onClick={handleWishlistClick}
              className="flex flex-col items-center gap-0.75 cursor-pointer text-primary hover:text-accent transition-colors"
              aria-label="Wishlist"
            >
              <div className="relative">
                <Heart className="w-4.75 h-4.75" strokeWidth={1.5} />
                {wishlistCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-accent text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold">
                    {wishlistCount}
                  </span>
                )}
              </div>
              <span className="text-[9.5px] text-header-muted hidden sm:block tracking-[0.3px]">Wishlist</span>
            </button>

            <button
              onClick={handleCartClick}
              className="flex flex-col items-center gap-0.75 cursor-pointer text-primary hover:text-accent transition-colors"
              aria-label="Cart"
            >
              <div className="relative">
                <ShoppingCart className="w-4.75 h-4.75" strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-accent text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold">
                    {cartCount}
                  </span>
                )}
              </div>
              <span className="text-[9.5px] text-header-muted hidden sm:block tracking-[0.3px]">Cart</span>
            </button>

            <button
              onClick={handleTrackingClick}
              className="flex flex-col items-center gap-0.75 cursor-pointer text-primary hover:text-accent transition-colors"
              aria-label="Track order"
            >
              <Truck className="w-4.75 h-4.75" strokeWidth={1.5} />
              <span className="text-[9.5px] text-header-muted hidden sm:block tracking-[0.3px]">Track</span>
            </button>

            <div className="relative user-menu-container">
              {user ? (
                <>
                  <button
                    onClick={() => setIsUserMenuOpen((v) => !v)}
                    className="flex flex-col items-center gap-0.75 cursor-pointer"
                    aria-label="User menu"
                  >
                    <div className="w-8.5 h-8.5 rounded-full bg-primary flex items-center justify-center text-white text-[13px] font-medium">
                      {(
                        user.user_metadata?.first_name?.charAt(0) ||
                        user.user_metadata?.full_name?.charAt(0) ||
                        user.email?.charAt(0)
                      )?.toUpperCase() || "U"}
                    </div>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-header-border shadow-xl z-50 py-2">
                      <div className="px-4 py-3 border-b border-header-border bg-header-surface">
                        <p className="text-[13px] font-semibold text-primary truncate">
                          {user.user_metadata?.full_name || user.user_metadata?.first_name || user.email?.split("@")[0] || "User"}
                        </p>
                        <p className="text-[11px] text-header-muted mt-0.5 truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={() => { signOut(); setIsUserMenuOpen(false); }}
                        className="w-full px-4 py-3 text-left text-[13px] text-primary hover:bg-header-surface hover:text-accent transition-colors flex items-center gap-3"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to="/registration"
                  className="flex flex-col items-center gap-0.75 cursor-pointer text-primary hover:text-accent transition-colors"
                >
                  <div className="w-8.5 h-8.5 rounded-full bg-primary flex items-center justify-center text-white text-[13px] font-medium">
                    <User className="w-4.75 h-4.75" strokeWidth={1.5} />
                  </div>
                  <span className="text-[9.5px] text-header-muted hidden sm:block tracking-[0.3px]">Sign In</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 3: categories nav ── */}
      {isCategoriesVisible && (
        <Categories
          categoriesLoading={categoriesLoading}
          categoriesError={categoriesError}
          categoriesWithSubs={categoriesWithSubs}
          categoryHoverDropdown={headerConfig.categoryHoverDropdown}
        />
      )}
    </div>
  );
};

export default Header;
